import * as THREE from 'three';
import { materialConfig } from '@/functions/shaft3dModel';
import { onBeforeUnmount, ref } from 'vue';
import { orientationHelper } from '@/functions/3dModel/orientationHelper.js';
import { ShaftBase, IssuePoint, scaleIssuePoint } from '@/functions/shaft3dModel';
import CameraControls from 'camera-controls';
import diameterOrDiagonal from '@/functions/shaft3dExtra';


CameraControls.install({ THREE: THREE });

let CAMERA;
let CAMERA_CONTROLS;
let SCENE_CONTAINER;

function setLights(scene) {
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
  directionalLight.position.set(10, 20, 0); // x, y, z
  scene.add(ambientLight, directionalLight);
}

function renderShaftBase(dimensions, scene) {
  const SHAFT_BASE = new ShaftBase(dimensions, 0x403F40);
  scene.add(SHAFT_BASE);
}

function handleResize(container, camera, renderer) {
  const widthOffset = container?.clientWidth;
  const heightOffset = container?.clientHeight;
  camera.aspect = widthOffset / heightOffset;
  camera.updateProjectionMatrix();
  renderer.setSize(widthOffset, heightOffset);
  renderer.setPixelRatio(window.devicePixelRatio);
}

function clearScene(scene) {
  scene.remove.apply(scene, scene.children);
}

function resetCamera() {
  CAMERA_CONTROLS.reset(true);
}

function focusOnElement(mesh) {
  CAMERA_CONTROLS.fitToBox(mesh, true, {
    paddingLeft: 1.5,
    paddingRight: 1.5,
    paddingBottom: 1.5,
    paddingTop: 1.5,
  });
  CAMERA_CONTROLS.rotate( 0, -0.35, true );
}

function disableCameraControls() {
  if (!CAMERA_CONTROLS) return;
  CAMERA_CONTROLS.enabled = false;
}

/**
 * Gets all the shaft elements or by specific type
 * @param {object} scene - the entire scene
 * @param {string} type - the type of element
 * @returns {array} array of all the elements based on type
 */
function getAllElements(scene, type) {
  return scene.children.filter((child) =>
    type ? child.userData.type === type : child.userData.element
  );
}

/**
 * Sets the element material type
 *
 * e.g.
 *  setElementMaterialType(element) //=> is the base material
 *  setElementMaterialType(element, 'ghosted') //=> is the ghosted material
 *
 * @param {object} element - the element to change
 * @param {string} type - type of material style eg 'ghosted'
 */
export function setElementMaterialType(element, type) {
  let ghostedMaterial = type === 'ghosted';

  // set opacity
  element.material.opacity = ghostedMaterial
    ? materialConfig.ghost.opacity
    : materialConfig.base.opacity;

  // set color
  element.material.color.set(
    ghostedMaterial ? materialConfig.ghost.color : element.userData.originalColor
  );
}

/**
 * Adds material to conveyance
 * @param {object} SCENE - the entire scene
 */
export function applyConveyanceStyle(SCENE) {
  getAllElements(SCENE).forEach((element) => {
    if(elementIsConveyance(element.userData.type)) {
      // apply color and opacity
      element.material.color.set(materialConfig.conveyance.color);
      element.material.opacity = materialConfig.conveyance.opacity;
    }
  });
}

function elementIsConveyance(elementType) {
  return elementType === 'skips_and_cages';
}

export function useShaft3dModel() {
  const RENDERER = new THREE.WebGLRenderer({ antialias: true });
  const SCENE = new THREE.Scene();
  SCENE.background = new THREE.Color(0x282728);
  const RAYCASTER = new THREE.Raycaster();
  const MOUSE = new THREE.Vector2();
  const PLANE_NORMAL = new THREE.Vector3();
  const INTERSECTION_POINT = new THREE.Vector3();
  const PLANE = new THREE.Plane();
  const CLOCK = new THREE.Clock();
  const elementHoverName = ref();
  const selectionByClick = ref(null);
  const pointSelection = ref(null);
  const sideWallsActive = ref(false);
  const flattenData = { scale: 0.1, positionY: 0 };

  function setCamera(target, dimensions) {
    const cameraValue = diameterOrDiagonal(dimensions);
    let camera = new THREE.PerspectiveCamera(
      80,
      (window.innerWidth - target.offsetWidth) / (window.innerHeight - target.offsetHeight),
      0.1,
      cameraValue * 100
    );

    camera.position.set(1, cameraValue / Math.PI, cameraValue / 1.2);
    camera.lookAt(0, 0, 0);
    camera.aspect = target.offsetWidth / target.offsetHeight;
    camera.updateProjectionMatrix();
    return camera;
  }

  function handlePointerMove(e, camera, targetContainer) {
    if (!targetContainer) return;
    MOUSE.x = ((e.clientX - targetContainer?.offsetLeft) / targetContainer?.clientWidth) * 2 - 1;
    MOUSE.y = -((e.clientY - targetContainer?.offsetTop) / targetContainer?.clientHeight) * 2 + 1;

    PLANE_NORMAL.copy(camera.position).normalize();
    PLANE.setFromNormalAndCoplanarPoint(PLANE_NORMAL, SCENE.position);
    RAYCASTER.setFromCamera(MOUSE, camera);
    RAYCASTER.ray.intersectPlane(PLANE, INTERSECTION_POINT);

    const intersects = RAYCASTER.intersectObjects(SCENE.children); // calculate objects intersecting the picking ray

    intersects.length > 0
      ? (elementHoverName.value = intersects[0].object.name)
      : (elementHoverName.value = null);
  }

  function handleElementSelect() {
    RAYCASTER.setFromCamera(MOUSE, CAMERA);
    const intersects = RAYCASTER.intersectObjects(SCENE.children);
    if (intersects.length > 0) {
      if (intersects[0].object.userData.inspectable === false) return;
      if (intersects[0].object.userData.element) {
        selectionByClick.value = intersects[0].object;
      }
      if (!intersects[0].object.userData) {
        selectionByClick.value = null;
      }
      if (intersects[0].object.userData.type === 'point') {
        pointSelection.value = intersects[0].object;
      }
    }
  }

  function handleClearNewIssuePoint() {
    SCENE.children.forEach(child => {
      if (child.userData.element) {
        child.children.forEach(point => {
          if (point.name === 'new point') {
            child.children.splice(child.children.indexOf(point), 1);
          }
        });
      }
    });
  }

  function handleAddIssuePoint(dimensions) {
    handleClearNewIssuePoint();

    RAYCASTER.setFromCamera(MOUSE, CAMERA);
    const intersects = RAYCASTER.intersectObjects(SCENE.children);

    if (intersects.length > 0) {
      let o = intersects[0];

      if (elementIsConveyance(o.object.userData.type)) return;

      if (o.object.userData.element) {
        let pIntersect = o.point.clone();
        intersects[0].object.worldToLocal(pIntersect);

        // Add new issue point to raycaster click
        let point = new IssuePoint();
        point.position.copy(o.face.normal).multiplyScalar(0).add(pIntersect);
        point = scaleIssuePoint(intersects[0].object, point, dimensions);
        intersects[0].object.add(point);

        return point;
      }
    }
  }

  function radiansToDegrees(radians){
    return radians * (180/Math.PI);
  }

  function renderScene() {
    const dir = new THREE.Vector3();
    const sph = new THREE.Spherical();

    CAMERA_CONTROLS.update(CLOCK.getDelta());
    CAMERA.getWorldDirection(dir);
    sph.setFromVector3(dir);

    const helper = document.getElementById('orientation');
    if (helper) {
      helper.style.transform = `rotate(${radiansToDegrees(sph.theta) - 180}deg)`;
    }

    requestAnimationFrame(renderScene);
    RENDERER.render(SCENE, CAMERA);
  }

  /**
   * Shows or hides the sidewalls
   * @param {boolean} elementIsSidewall - Boolean value
   */
  function displaySidewalls(elementIsSidewall) {
    if (elementIsSidewall) {
      sideWallsActive.value = true;
      // TODO: make this better
      getAllElements(SCENE, 'sidewalls').forEach((element) => {
        element.scale.y = element.userData.originalPosition.height;
        element.position.y = element.userData.originalPosition.y;
        element.visible = true;
      });
    } else {
      sideWallsActive.value = false;
      // TODO: make this better
      getAllElements(SCENE, 'sidewalls').forEach((element) => {
        element.scale.y = flattenData.scale;
        element.position.y = flattenData.positionY;
        element.visible = false;
      });
    }
  }

  /**
   * Expands or collapses the shaft elements
   * @param {string} value - The emitted value on the click handler
   */
  function flattenElements(value) {
    getAllElements(SCENE)?.forEach((element) => {
      if (value === 'expand') {
        element.scale.y = element.userData.originalPosition.height;
        element.position.y = element.userData.originalPosition.y;
      } else if (value === 'collapse') {
        element.scale.y = flattenData.scale;
        element.position.y = flattenData.positionY;
      }
    });
  }

  function initTHREE(targetContainer, dimensions) {
    if (!targetContainer) return;

    SCENE_CONTAINER = targetContainer;

    CAMERA = setCamera(SCENE_CONTAINER, dimensions);
    CAMERA_CONTROLS = new CameraControls(CAMERA, RENDERER.domElement);
    CAMERA_CONTROLS.draggingDampingFactor = 10;
    CAMERA_CONTROLS.dollyToCursor = true;

    CAMERA_CONTROLS.maxDistance = diameterOrDiagonal(dimensions);
    setLights(SCENE);
    RENDERER.setSize(SCENE_CONTAINER.offsetWidth, SCENE_CONTAINER.offsetHeight);

    SCENE_CONTAINER.addEventListener('mousemove', (e) => handlePointerMove(e, CAMERA, SCENE_CONTAINER));
    SCENE_CONTAINER.addEventListener('mouseleave', () => elementHoverName.value = null);
    window.addEventListener('resize', () => handleResize(SCENE_CONTAINER, CAMERA, RENDERER));

    let dragging = false;

    SCENE_CONTAINER.addEventListener('mousedown', () => (dragging = false));
    SCENE_CONTAINER.addEventListener('mousemove', () => (dragging = true));
    SCENE_CONTAINER.addEventListener('mouseup', () => {
      if (!dragging) {
        handleElementSelect();
      }
    });

    SCENE_CONTAINER.appendChild(RENDERER.domElement);

    renderScene();
    renderShaftBase(dimensions, SCENE);
    SCENE.add(orientationHelper(diameterOrDiagonal(dimensions, 1)));
  }

  onBeforeUnmount(() => {
    if (SCENE_CONTAINER) {
      SCENE_CONTAINER.removeEventListener('mousemove', handlePointerMove);
      SCENE_CONTAINER.removeEventListener('mouseup', handleElementSelect);
      window.removeEventListener('resize', handleResize);
      clearScene(SCENE);
    }
  });

  return {
    SCENE,
    initTHREE,
    elementHoverName,
    clearScene,
    resetCamera,
    focusOnElement,
    selectionByClick,
    pointSelection,
    handleAddIssuePoint,
    handleClearNewIssuePoint,
    getAllElements,
    disableCameraControls,
    setElementMaterialType,
    applyConveyanceStyle,
    displaySidewalls,
    flattenElements,
    sideWallsActive
  };
}
